import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import api from '../api';
import { generateKeyPair, storeKeyPair, getKeyPair, encryptMessage, decryptMessage } from '../utils/crypto';

export default function ChatView({ roomId, onBack }) {
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [peerTyping, setPeerTyping] = useState(false);
  const peerTypingTimeout = useRef(null);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeout = useRef(null);

  const myKeys = useRef(getKeyPair(roomId));

  // Get peer info from room
  const peer = room?.participants?.find(p => p.user !== user._id);
  const me = room?.participants?.find(p => p.user === user._id);

  useEffect(() => {
    // If no keys stored, generate and set
    if (!myKeys.current) {
      const kp = generateKeyPair();
      myKeys.current = kp;
      storeKeyPair(roomId, kp);
    }

    // Fetch room and messages
    Promise.all([
      api.get(`/ghost-hours/rooms/${roomId}`),
      api.get(`/ghost-hours/rooms/${roomId}/messages`)
    ]).then(async ([roomRes, msgRes]) => {
      const roomData = roomRes.data.room;
      setRoom(roomData);
      setMessages(msgRes.data.messages);

      // Ensure our public key is set on the room
      const myParticipant = roomData.participants.find(p => p.user === user._id);
      if (!myParticipant.publicKey) {
        await api.put(`/ghost-hours/rooms/${roomId}/key`, { publicKey: myKeys.current.publicKey });
      }
    }).catch(() => {}).finally(() => setLoading(false));

    // Socket connection
    const token = localStorage.getItem('accessToken');
    const socket = io(window.location.origin, {
      path: '/api/socket.io',
      auth: { token }
    });
    socketRef.current = socket;

    socket.on('connect', () => { socket.emit('join_room', roomId); });
    socket.on('new_message', (msg) => { setMessages(prev => [...prev, msg]); setPeerTyping(false); });
    socket.on('peer_typing', () => {
      setPeerTyping(true);
      clearTimeout(peerTypingTimeout.current);
      peerTypingTimeout.current = setTimeout(() => setPeerTyping(false), 3000);
    });
    socket.on('peer_stop_typing', () => { setPeerTyping(false); });

    return () => { clearTimeout(peerTypingTimeout.current); socket.disconnect(); };
  }, [roomId, user._id]); // eslint-disable-line

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const decryptMsg = useCallback((msg) => {
    if (!myKeys.current || !peer?.publicKey) return '[encrypted]';
    const text = decryptMessage(msg.encryptedContent, msg.nonce, peer.publicKey, myKeys.current.secretKey);
    return text || '[unable to decrypt]';
  }, [peer?.publicKey]);

  const sendMessage = () => {
    if (!draft.trim() || !peer?.publicKey || !myKeys.current) return;

    const { encryptedContent, nonce } = encryptMessage(draft.trim(), peer.publicKey, myKeys.current.secretKey);

    socketRef.current?.emit('send_message', {
      roomId,
      encryptedContent,
      nonce,
      senderAlias: user.alias
    });

    setDraft('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    socketRef.current?.emit('stop_typing', { roomId, alias: user.alias });
  };

  const handleTyping = (e) => {
    setDraft(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(120, e.target.scrollHeight) + 'px';
    socketRef.current?.emit('typing', { roomId, alias: user.alias });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { roomId, alias: user.alias });
    }, 2000);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReport = async (msg) => {
    if (!window.confirm('Report this message? The decrypted content will be shared with moderators.')) return;
    const plaintext = decryptMsg(msg);
    if (plaintext === '[encrypted]' || plaintext === '[unable to decrypt]') return;
    try {
      await api.post(`/ghost-hours/rooms/${roomId}/report`, { messageId: msg._id, plaintext });
      alert('Message reported to moderation team.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to report');
    }
  };

  const handleUnmask = async () => {
    if (!window.confirm('Consent to unmasking? If your peer also consents, you will both see each other\'s real name. This cannot be undone.')) return;
    try {
      const { data } = await api.post(`/ghost-hours/rooms/${roomId}/unmask`);
      setRoom(data.room);
    } catch {}
  };

  const handleClose = async () => {
    if (!window.confirm('Close this conversation?')) return;
    await api.post(`/ghost-hours/rooms/${roomId}/close`);
    onBack();
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: tokens.ground,
      }}>
        <p style={{
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 16, color: tokens.textMid,
        }}>Loading...</p>
      </div>
    );
  }

  const waitingForKey = !peer?.publicKey;

  // Build transcript turns
  const renderMessages = () => {
    return messages.map((msg, i) => {
      const isMe = msg.sender === user._id;
      const text = decryptMsg(msg);
      const alias = isMe ? 'you' : (msg.senderAlias || peer?.alias || 'Peer');
      const prev = messages[i - 1];
      const newTurn = !prev || prev.sender !== msg.sender;
      const time = msg.createdAt
        ? new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;

      return (
        <div key={msg._id || i} className="ghost-msg-row" style={{
          marginTop: newTurn && i > 0 ? 28 : (i > 0 ? 10 : 0),
          animation: `gl-fade-up 500ms ${tokens.ease} ${Math.min(i, 20) * 60}ms both`,
        }}>
          {newTurn && (
            <div style={{
              fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: isMe ? tokens.textLow : tokens.accent,
              opacity: isMe ? 0.7 : 1,
              marginBottom: 6,
              display: 'flex', alignItems: 'baseline', gap: 8,
            }}>
              <span>{alias}{time ? ` \u00b7 ${time}` : ''}</span>
              {!isMe && (
                <button
                  className="ghost-report-btn"
                  onClick={() => handleReport(msg)}
                  style={{
                    fontSize: 9, color: tokens.textDim,
                    fontFamily: tokens.fontMono, background: 'none',
                    border: 'none', cursor: 'pointer', padding: '0 2px',
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}
                >
                  report
                </button>
              )}
            </div>
          )}
          <div style={{
            fontFamily: tokens.fontBody, fontSize: 16, lineHeight: 1.65,
            color: isMe ? tokens.textMid : tokens.textHi,
            maxWidth: '92%', whiteSpace: 'pre-wrap',
          }}>
            {text}
          </div>
        </div>
      );
    });
  };

  return (
    <div style={{
      position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: tokens.ground,
    }}>
      <style>{`
        .ghost-report-btn { opacity: 0; transition: opacity 150ms ease; }
        .ghost-msg-row:hover .ghost-report-btn { opacity: 1; }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '16px 22px', position: 'sticky', top: 0, zIndex: 15,
        background: tokens.ground, borderBottom: `1px solid ${tokens.edge}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="gl-btn-bare" onClick={onBack} style={{
            fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em',
            color: tokens.textMid, textTransform: 'uppercase',
          }}>&larr; Close</button>
          <span style={{ flex: 1 }} />
          {/* Unmask / Close actions */}
          {!me?.unmaskConsent && (
            <button className="gl-btn-bare" onClick={handleUnmask} style={{
              fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.12em',
              color: tokens.textDim, textTransform: 'uppercase', marginRight: 12,
            }}>unmask</button>
          )}
          {me?.unmaskConsent && !me?.unmasked && (
            <span style={{
              fontFamily: tokens.fontMono, fontSize: 10, color: tokens.textDim,
              fontStyle: 'italic', marginRight: 12,
            }}>waiting...</span>
          )}
          <button className="gl-btn-bare" onClick={handleClose} style={{
            fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.12em',
            color: tokens.textDim, textTransform: 'uppercase', marginRight: 12,
          }}>end</button>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: tokens.calm,
            boxShadow: `0 0 6px ${tokens.calm}`,
            animation: 'gl-breathe 2.5s ease-in-out infinite',
          }} />
          <span style={{
            fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.14em',
            textTransform: 'uppercase', color: tokens.textLow,
          }}>
            End-to-end
          </span>
        </div>
        <div style={{
          marginTop: 10, fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 16, color: tokens.textMid,
        }}>
          {peer?.alias || 'Peer'} is here.
          {peer?.unmasked && me?.unmasked && (
            <span style={{ color: tokens.textLow, fontSize: 14, marginLeft: 8 }}>
              ({peer?.realName})
            </span>
          )}
        </div>
      </div>

      {/* Transcript area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 22px 24px' }}>
        {waitingForKey && (
          <p style={{
            fontFamily: tokens.fontDisplay, fontStyle: 'italic',
            fontSize: 15, color: tokens.textMid,
          }}>
            Waiting for peer to join...
          </p>
        )}

        {!waitingForKey && messages.length > 0 && decryptMsg(messages[0]) === '[unable to decrypt]' && (
          <div style={{ marginBottom: 28 }}>
            <p style={{
              fontFamily: tokens.fontBody, fontStyle: 'italic',
              fontSize: 13, color: tokens.textLow, margin: '0 0 4px',
            }}>
              Previous messages can't be decrypted
            </p>
            <p style={{
              fontFamily: tokens.fontBody, fontSize: 12, color: tokens.textDim, margin: 0,
            }}>
              Ghost Hours conversations are ephemeral. New messages will work normally.
            </p>
          </div>
        )}

        {renderMessages()}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {peerTyping && (
        <div style={{
          padding: '6px 22px',
          fontFamily: tokens.fontDisplay, fontStyle: 'italic',
          fontSize: 13, color: tokens.textLow,
          animation: `gl-fade 200ms ${tokens.ease}`,
        }}>
          {peer?.alias || 'Peer'} is typing...
        </div>
      )}

      {/* Composer */}
      <div style={{
        padding: '12px 22px 18px', borderTop: `1px solid ${tokens.edge}`,
        background: tokens.ground,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={handleTyping}
            onKeyDown={handleKeyDown}
            placeholder={waitingForKey ? 'Waiting for peer...' : 'What do you want to say?'}
            disabled={waitingForKey}
            rows={1}
            maxLength={5000}
            style={{
              flex: 1, background: 'transparent', border: 'none', resize: 'none', outline: 'none',
              color: tokens.textHi, fontFamily: tokens.fontBody, fontSize: 15,
              lineHeight: 1.5, padding: '10px 0',
              fontStyle: draft ? 'normal' : 'italic',
              opacity: waitingForKey ? 0.4 : 1,
            }}
          />
          <button className="gl-btn-bare" onClick={sendMessage} disabled={waitingForKey || !draft.trim()} style={{
            fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.16em',
            textTransform: 'uppercase', padding: '12px 4px',
            color: draft.trim() ? tokens.accent : tokens.textDim,
            transition: `color 200ms ${tokens.ease}`,
          }}>Send</button>
        </div>
      </div>
    </div>
  );
}
